import { Api, IApiResponse } from '../api';
import { AdminApplicationDto, ApplicationDto, ApplicationEntryDto, ApplicationLocationUsageStatDto, ApplicationRequestStatDto, ApplicationUrlDto, ApplicationUsageStatDto, CategoryDto } from '../models';
import Fuse from 'fuse.js';

export interface ILocationApplication {
    id: string;
    appId: string;
    appName: string;
    description: string;
    moreInformationUrl: string;
    locationCode?: string | null;
    url: string;
    iconUrl: string;
    isArchived: boolean;
    tags: string[];
    categories: CategoryViewModel[];
}

export interface IApplicationLocationsAndCategories {
    applications: ILocationApplication[];
    categoriesById: { [categoryId: string]: CategoryViewModel };
}

export class CategoryViewModel {
    private collapsed = false;
    constructor(private category: CategoryDto) {}

    public get name() {
        return this.category.name;
    }

    public get id() {
        return this.category.id;
    }

    public get isCollapsed() {
        return this.collapsed;
    }

    public toggle() {
        this.collapsed = !this.collapsed;
    }
}

const generateCategoryViewModels = (applications: ApplicationDto[]) => {
    return applications.reduce((categoryVmById: { [categoryId: string]: CategoryViewModel }, item) => {
        item.categories
            .filter((c) => !categoryVmById[c.id])
            .map((newCategory) => (categoryVmById[newCategory.id] = new CategoryViewModel(newCategory)));
        return categoryVmById;
    }, {});
};

const getCategoryViews = (categories: CategoryDto[], categoryViewModels: { [categoryId: string]: CategoryViewModel }): CategoryViewModel[] => {
    return categories.map((category) => categoryViewModels[category.id]);
};

const getMine = async (signal?: AbortSignal, accessToken?: string): Promise<IApiResponse<ApplicationDto[]>> => Api.get(`apps/myapps`, signal, accessToken);

const getMineFlat = async (signal?: AbortSignal, accessToken?: string): Promise<IApiResponse<IApplicationLocationsAndCategories>> => {
    const response = await getMine(signal, accessToken);
    if (!response.success || !response.result) {
        return { success: false };
    }
    const applications = response.result;
    const categoryViewModels = generateCategoryViewModels(applications);
    const myApplications: ILocationApplication[] = [];
    applications.forEach((item) => {
        const app = item.urls.map(
            (url: ApplicationUrlDto): ILocationApplication => ({
                id: `${item.id}-${url.locationCode ? url.locationCode : 'global'}`,
                appId: item.id,
                appName: item.urls.length > 1 ? `${item.name} (${url.locationName})` : item.name,
                description: item.description,
                moreInformationUrl: item.moreInformationUrl,
                url: url.url,
                locationCode: url.locationCode,
                iconUrl: item.iconUrl,
                // server already excludes archived apps from this endpoint
                isArchived: false,
                tags: item.tags,
                categories: getCategoryViews(item.categories, categoryViewModels),
            }),
        );
        myApplications.push(app[0]);
    });

    return { success: true, result: { applications: myApplications, categoriesById: categoryViewModels } };
};

const getMany = (signal?: AbortSignal, accessToken?: string): Promise<IApiResponse<ApplicationDto[]>> => Api.get('apps', signal, accessToken);

const getManyAdmin = (signal?: AbortSignal, accessToken?: string): Promise<IApiResponse<AdminApplicationDto[]>> => Api.get('apps/admin', signal, accessToken);

const getManyFlat = async (signal?: AbortSignal, accessToken?: string): Promise<IApiResponse<IApplicationLocationsAndCategories>> => {
    const response = await getMany(signal, accessToken);
    if (!response.success || !response.result) {
        return { success: false };
    }

    const applications = response.result;

    const categoryViewModels = generateCategoryViewModels(applications);

    const locationApplications = applications.reduce(
        (list: ILocationApplication[], item: ApplicationDto) =>
            list.concat(
                item.urls.map(
                    (url: ApplicationUrlDto): ILocationApplication => ({
                        id: `${item.id}-${url.locationCode ? url.locationCode : 'global'}`,
                        appId: item.id,
                        appName: item.urls.length > 1 ? `${item.name} (${url.locationName})` : item.name,
                        description: item.description,
                        moreInformationUrl: item.moreInformationUrl,
                        url: url.url,
                        locationCode: url.locationCode,
                        iconUrl: item.iconUrl,
                        // server already excludes archived apps from this endpoint
                        isArchived: false,
                        tags: item.tags,
                        categories: getCategoryViews(item.categories, categoryViewModels),
                    }),
                ),
            ),
        [],
    );

    return { success: true, result: { applications: locationApplications, categoriesById: categoryViewModels } };
};

const get = (id: string, signal?: AbortSignal): Promise<IApiResponse<ApplicationDto>> => Api.get(`apps/${id}`, signal);

const getAdmin = (id: string, signal?: AbortSignal): Promise<IApiResponse<AdminApplicationDto>> => Api.get(`apps/admin/${id}`, signal);

const add = (application: ApplicationEntryDto, iconFile: File | undefined, signal?: AbortSignal) =>
    Api.post('Apps', application, signal, iconFile ? [iconFile] : []);

const update = (id: string, application: ApplicationEntryDto, iconFile: File | undefined, signal?: AbortSignal) =>
    Api.put(`Apps/${id}`, application, signal, iconFile ? [iconFile] : []);

const getApplicationUsageStats = (signal?: AbortSignal, accessToken?: string): Promise<IApiResponse<ApplicationUsageStatDto[]>> =>
    Api.get('Apps/usage-stats', signal, accessToken);

const getApplicationLocationUsageStats = (signal?: AbortSignal, accessToken?: string): Promise<IApiResponse<ApplicationLocationUsageStatDto[]>> =>
    Api.get('Apps/usage-stats/by-location', signal, accessToken);

const getApplicationRequestStats = (signal?: AbortSignal, accessToken?: string): Promise<IApiResponse<ApplicationRequestStatDto[]>> =>
    Api.get('Apps/request-stats', signal, accessToken);

export type LaunchFormSource = 'Category' | 'AZ' | 'SideDraw' | 'Search';

const recordLaunch = (
    appId: string,
    locationCode?: string | null,
    categoryId?: string | null,
    form?: LaunchFormSource | string,
    signal?: AbortSignal,
    accessToken?: string,
): Promise<IApiResponse<unknown>> => {
    const params = new URLSearchParams();
    if (locationCode) {
        params.append('locationCode', locationCode);
    }
    if (categoryId) {
        params.append('categoryId', categoryId);
    }
    if (form) {
        params.append('form', form);
    }
    const query = params.toString();
    const path = query ? `Apps/${appId}/launch?${query}` : `Apps/${appId}/launch`;
    return Api.get(path, signal, accessToken);
};

const nameCollator = new Intl.Collator('en', { sensitivity: 'base' });

const sortApplications = (applications: ILocationApplication[]): ILocationApplication[] =>
    [...applications].sort((first, second) => nameCollator.compare(first.appName, second.appName) || first.id.localeCompare(second.id, 'en'));

const filterApplications = (applications: ILocationApplication[] | null, searchPhrase: string | null): ILocationApplication[] => {
    if (!applications) {
        return [];
    }

    const searchTerm = searchPhrase?.trim().replace(/\s+/g, ' ') ?? '';
    if (!searchTerm) {
        return sortApplications(applications);
    }

    const fuse = new Fuse(applications, {
        keys: ['appName', 'description', 'tags', 'categories.name'],
        threshold: 0.35,
        ignoreLocation: true,
    });
    return sortApplications(fuse.search(searchTerm).map(({ item }) => item));
};

export { getMany, getManyAdmin, getManyFlat, getMineFlat, get, getAdmin, add, update, getApplicationUsageStats, getApplicationLocationUsageStats, getApplicationRequestStats, recordLaunch, filterApplications };
